import { Building2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { type BuInstance, useBuInstances, useDeleteBuInstance } from "@/hooks/use-bu-instances";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { BuInstanceCreateDialog } from "./BuInstanceCreateDialog";
import { BuInstanceEditDialog } from "./BuInstanceEditDialog";

// `grid.status`/`setStatus` is the shared data-grid filter slot (named
// "status" by useDataGrid/buildListParams); here it carries synthetic
// "active"/"inactive" values mapped to the bu_instance.active boolean
// server-side (see src/api/bu-instances.ts).
const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/**
 * The BU Instances admin grid — mirrors `src/examples/widget/WidgetsTable.tsx`
 * verbatim (search/status/sort/cursor state owned entirely by `useDataGrid`).
 */
export function BuInstancesTable() {
  const { label, labelPlural } = useBuInstanceLabel();
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BuInstance | null>(null);

  const { data, isLoading, isError, error, refetch } = useBuInstances(grid.query, grid.pageRequest);
  const deleteBuInstance = useDeleteBuInstance();

  const buInstances = data?.data ?? [];

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteBuInstance.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder={`Search ${labelPlural}...`}
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={STATUS_FILTER_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New {label}
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? `Failed to load ${labelPlural}`}
          onRetry={() => refetch()}
        />
        <BuInstanceCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Name", sortKey: "name" },
          { label: "Endpoint" },
          { label: "Username" },
          { label: "Status", sortKey: "active" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Building2}
            title={`No ${labelPlural} found`}
            description="Try adjusting your search or filters."
          />
        }
      >
        {buInstances.map((buInstance) => (
          <tr
            key={buInstance.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{buInstance.name}</td>
            <td className="px-4 py-3 text-muted-foreground font-mono text-xs truncate max-w-xs">
              {buInstance.endpointUrl}
            </td>
            <td className="px-4 py-3 text-muted-foreground">{buInstance.username}</td>
            <td className="px-4 py-3">
              <StatusBadge status={buInstance.active ? "active" : "inactive"} />
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  {
                    label: "Edit",
                    icon: Pencil,
                    onSelect: () => setEditId(buInstance.id),
                  },
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(buInstance),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel={labelPlural}
      />

      <BuInstanceCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BuInstanceEditDialog id={editId} onOpenChange={(open) => !open && setEditId(null)} />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={`Delete ${label}`}
        description={`Delete ${deleteTarget?.name ?? `this ${label}`}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteBuInstance.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
